/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REVIEW_USER_FORM_SPLIT
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Refactored ReviewUserForm into sub-components to comply with file size limits.
 * Main orchestrator component that uses extracted UI pieces.
 *
 * File Size Check:
 * - Current: ~100 lines
 * - Limit: 200 lines
 * - Decision: Splitting complete (orchestrator is well under limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/review-user/components/ReviewUserHeader.tsx
 * - client/src/components/setup/review-user/components/ReviewUserAbout.tsx
 * - client/src/components/setup/review-user/components/ReviewUserSignature.tsx
 * - client/src/components/setup/review-common/components/ReviewUserContact.tsx
 */
import React from "react";
import { Row, Col, Card, Spinner } from "react-bootstrap";
import { useSetup } from "../../Setup";
import type { ImageData } from "../../../interfaces/User";
import { ReviewUserHeader } from "./components/ReviewUserHeader";
import { ReviewUserAbout } from "./components/ReviewUserAbout";
import { ReviewUserSignature } from "./components/ReviewUserSignature";
import { ReviewUserContact } from "../review-common/components/ReviewUserContact";
import { useNavigate } from "react-router-dom";

const getImageSrc = (img?: ImageData | null): string | null => {
  if (!img) return null;
  return img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`;
};

const ReviewUserForm: React.FC = () => {
  const { profile, loading, error } = useSetup();
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate("/setup?section=profile");
  };

  if (loading && !profile) {
    return (
      <div className="review-page-loading d-flex justify-content-center align-items-center">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: "100%", width: "100%" }}>
      <div className="review-page">
        <Card className="review-card mb-3">
          <Card.Body>
            <ReviewUserHeader 
              profile={profile} 
              onEdit={handleEdit} 
              getImageSrc={getImageSrc} 
            />
          </Card.Body>
        </Card>

        <Row className="review-section" style={{ minWidth: 0 }}>
          <Col md={8} style={{ minWidth: 0 }}>
            <Card className="review-card mb-3" style={{ width: "100%", maxWidth: "100%" }}>
              <Card.Body>
                <ReviewUserAbout profile={profile} />
              </Card.Body>
            </Card>

            <ReviewUserSignature profile={profile} />
          </Col>

          <Col md={4} style={{ minWidth: 0 }}>
            <ReviewUserContact profile={profile} />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ReviewUserForm;
